import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Dialog } from "@/components/common/Dialog";
import { logout } from "@/auth/actions";
import { useMe } from "@/auth/use-me";
import { DeletePersonDocument } from "@/graphql/generated/operations";
import { logApplicationFailure } from "@/lib/user-error";

// Settings · Konto — irreversible account deletion (genesis deletePerson).
// Mirrors discovery_app: the user must type their full name to confirm, then
// the session is ended (logout → redirect). Lives in a "danger zone" card.
export function DeleteAccountCard() {
  const intl = useIntl();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deletePerson, { loading }] = useMutation(DeletePersonDocument);

  const expectedName = me.fullName.trim();
  const canDelete =
    expectedName.length > 0 &&
    confirmText.trim().toLowerCase() === expectedName.toLowerCase() &&
    Boolean(me.id);

  function openDialog() {
    setConfirmText("");
    setError(null);
    setOpen(true);
  }

  async function confirmDelete() {
    if (!canDelete || !me.id || loading) return;
    setError(null);
    try {
      const res = await deletePerson({ variables: { input: { id: me.id, dryRun: false } } });
      if (!res.data?.deletePerson?.id) throw new Error("deletePerson returned no id");
      // Account is gone — end the session and bounce to the start.
      await logout();
    } catch {
      logApplicationFailure("settings.delete-account");
      setError(
        intl.formatMessage({
          id: "settings.account.delete.error",
          defaultMessage: "Deletion failed. Please try again.",
        }),
      );
    }
  }

  return (
    <div className="box bg-surface p-6">
      <div className="text-[10px] tracking-[0.25em] text-red-600/80">
        {intl.formatMessage({ id: "settings.account.delete.zone", defaultMessage: "Danger zone" })}
      </div>
      <div className="mt-3 flex flex-col gap-3 @sm:flex-row @sm:items-center @sm:justify-between">
        <div className="max-w-md">
          <div className="field-value font-medium">
            {intl.formatMessage({ id: "settings.account.delete.title", defaultMessage: "Delete account" })}
          </div>
          <div className="mt-1 text-[12px] text-ink/55">
            {intl.formatMessage({
              id: "settings.account.delete.desc",
              defaultMessage:
                "Your account and all associated data are permanently deleted. This cannot be undone.",
            })}
          </div>
        </div>
        <Button variant="destructive" onClick={openDialog} className="shrink-0">
          {intl.formatMessage({ id: "settings.account.delete.button", defaultMessage: "Delete account" })}
        </Button>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={intl.formatMessage({
          id: "settings.account.delete.dialogTitle",
          defaultMessage: "Permanently delete account?",
        })}
      >
        <p className="text-sm text-ink/70">
          {intl.formatMessage({
            id: "settings.account.delete.desc",
            defaultMessage:
              "Your account and all associated data are permanently deleted. This cannot be undone.",
          })}
        </p>
        <label className="mt-4 block text-[12px] text-ink/70">
          {intl.formatMessage({
            id: "settings.account.delete.prompt",
            defaultMessage: "Type your full name to confirm:",
          })}{" "}
          <strong className="text-ink">{expectedName || "–"}</strong>
        </label>
        <input
          autoFocus
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={intl.formatMessage({
            id: "settings.account.delete.placeholder",
            defaultMessage: "Full name",
          })}
          className="mt-2 w-full rounded-md bg-page px-3 py-2 text-sm outline-none ring-1 ring-ink/10 transition focus:ring-accent/40"
        />
        {error ? <div className="mt-2 text-[12px] text-red-600">{error}</div> : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
          </Button>
          <Button
            variant="destructive"
            disabled={!canDelete || loading}
            onClick={() => void confirmDelete()}
            className="disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading
              ? intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" })
              : intl.formatMessage({
                  id: "settings.account.delete.confirm",
                  defaultMessage: "Delete permanently",
                })}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
